import { useState, useEffect } from 'react';
import { fetchPuestos, crearPersonaAsignacion } from '../../../../shared/services/api';
import {
  modalOverlay,
  modalBox,
  fieldLabel,
  fieldInput,
  btnCancel,
  btnPrimary,
} from './persona-detail-shared';

export function AssignToPuestoModal({
  personaId,
  onClose,
  onAssigned,
}: {
  personaId: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<{ id: string; titulo: string; codigo: string }>>([]);
  const [selected, setSelected] = useState<{ id: string; label: string } | null>(null);
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fte, setFte] = useState('1');
  const [esPrincipal, setEsPrincipal] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (search.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      fetchPuestos({ q: search, porPagina: '10' })
        .then((r) =>
          setResults(
            (r.data ?? []).map((pp) => ({
              id: pp.id,
              titulo: pp.titulo,
              codigo: pp.codigo ?? '',
            })),
          ),
        )
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleAssign = async () => {
    if (!selected) {
      setError('Selecciona un puesto');
      return;
    }
    try {
      setSaving(true);
      await crearPersonaAsignacion(personaId, {
        puestoId: selected.id,
        fechaInicio,
        fte: parseFloat(fte) || 1,
        esPrincipal,
      });
      onAssigned();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={modalOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0E243E' }}>
          Asignar a puesto
        </h2>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabel}>Buscar puesto</label>
          {selected ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#F0F4FA',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
              }}
            >
              <span style={{ flex: 1, fontSize: 13 }}>{selected.label}</span>
              <button
                onClick={() => {
                  setSelected(null);
                  setSearch('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#DC2626',
                  fontSize: 16,
                  fontFamily: 'inherit',
                }}
              >
                x
              </button>
            </div>
          ) : (
            <>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Titulo o codigo..."
                style={fieldInput}
              />
              {results.length > 0 && (
                <div
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 150,
                    overflow: 'auto',
                  }}
                >
                  {results.map((r) => (
                    <div
                      key={r.id}
                      onClick={() => {
                        setSelected({ id: r.id, label: `${r.titulo} (${r.codigo})` });
                        setResults([]);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: 13,
                        borderBottom: '1px solid #F3F4F6',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F4FA')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                    >
                      {r.titulo}{' '}
                      <span style={{ color: '#9CA3AF', fontSize: 11 }}>({r.codigo})</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px 16px',
            marginBottom: 12,
          }}
        >
          <div>
            <label style={fieldLabel}>Fecha inicio *</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              style={fieldInput}
            />
          </div>
          <div>
            <label style={fieldLabel}>FTE (0-1)</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={fte}
              onChange={(e) => setFte(e.target.value)}
              style={fieldInput}
            />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={esPrincipal}
              onChange={(e) => setEsPrincipal(e.target.checked)}
            />
            Es principal
          </label>
        </div>
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnCancel}>
            Cancelar
          </button>
          <button
            onClick={handleAssign}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Asignando...' : 'Asignar'}
          </button>
        </div>
      </div>
    </div>
  );
}
